// Canonical service list — docs/brand-resources/COMPANY_PROFILE_EXTRACT + .cursor/rules/360dg-brand.mdc
// Odoo is featured separately; all other services share the same name everywhere.

/** Official Odoo brand palette — use only for Odoo/partner UI */
export const ODOO_PURPLE = '#714B67';
export const ODOO_TEAL = '#017E84';
export const ODOO_PARTNER_TEAL = '#21B799';

/** Orbit radii — inner (Odoo) → outer (AI), degrees evenly spaced from top */
const ORBIT_RINGS = [92, 112, 132, 152, 172, 192, 212];
const ORBIT_ANGLES = [-90, -39, 12, 63, 114, 165, 216];

export const ODOO_SERVICE = {
  id: 'odoo',
  name: 'odoo ERP Implementation',
  navLabel: 'odoo ERP',
  scrollTarget: 'odoo',
  featured: true,
  color: ODOO_PURPLE,
  iconId: 'odoo',
  tagline: 'One connected ERP for operations, finance, sales, inventory, HR, and growth.',
  description:
    'Discovery, configuration, migration, training, and support for teams that want odoo implemented cleanly.',
  features: [
    'Implementation Roadmap',
    'Module Configuration',
    'Custom Development',
    'UAE VAT / WPS Setup',
    'Data Migration',
    'Training & Support',
  ],
};

export const SUPPORTING_SERVICES = [
  {
    id: 'branding',
    name: 'Branding & Re-Branding',
    description: 'Brand strategy, identity systems, messaging, guidelines, and re-branding support built for consistent execution.',
    iconId: 'branding',
    color: '#E42613',
    kpi: '02',
    kpiLabel: 'service',
    scrollTarget: 'services',
    features: ['Brand positioning', 'Logo and identity system', 'Messaging kit', 'Guideline assets'],
  },
  {
    id: 'web',
    name: 'Web Design & Content Creation',
    description: 'Responsive websites, UX, e-commerce, SEO-ready content, copywriting, and visual storytelling.',
    iconId: 'web',
    color: '#0047AB',
    kpi: '03',
    kpiLabel: 'service',
    scrollTarget: 'services',
    features: ['UX and page structure', 'E-commerce journeys', 'SEO content direction', 'Launch-ready copy'],
  },
  {
    id: 'marketing',
    name: 'Digital Marketing & Social Media',
    description: 'Paid, SEO, content, and social campaigns planned around visibility, qualified leads, and conversion.',
    iconId: 'marketing',
    color: '#F25C2E',
    kpi: '04',
    kpiLabel: 'service',
    scrollTarget: 'services',
    features: ['Campaign strategy', 'SEO and content', 'Paid growth loops', 'Social publishing'],
  },
  {
    id: 'accounting',
    name: 'Accounting as a Service',
    description: 'Bookkeeping, VAT, payroll, reconciliations, and monthly reporting support for UAE operations.',
    iconId: 'accounting',
    color: '#38AECC',
    kpi: '05',
    kpiLabel: 'service',
    scrollTarget: 'services',
    features: ['Bookkeeping support', 'VAT and compliance', 'Payroll coordination', 'Monthly management reports'],
  },
  {
    id: 'apps',
    name: 'Tailor-Made Apps',
    description: 'Custom odoo modules, mobile companion apps, portals, workflow tools, and system integrations.',
    iconId: 'apps',
    color: '#B64B8A',
    kpi: '06',
    kpiLabel: 'service',
    scrollTarget: 'services',
    features: ['Custom odoo modules', 'Client and team portals', 'Workflow tools', 'API integrations'],
  },
  {
    id: 'ai',
    name: 'AI Agents, Engines & Platforms',
    description:
      'AI agents, automation engines, and data-aware platforms that reduce manual work, route tasks, and scale service.',
    iconId: 'ai',
    color: ODOO_TEAL,
    kpi: '07',
    kpiLabel: 'service',
    scrollTarget: 'services',
    features: ['AI agent workflows', 'Document and task routing', 'Data-aware platforms', 'Human approval loops'],
  },
];

export const SERVICE_COUNT = 1 + SUPPORTING_SERVICES.length;

export const ALL_SERVICE_NAMES = [ODOO_SERVICE.name, ...SUPPORTING_SERVICES.map((s) => s.name)];

export function getServiceById(id) {
  if (id === ODOO_SERVICE.id) return ODOO_SERVICE;
  return SUPPORTING_SERVICES.find((s) => s.id === id);
}

export function getServiceLabel(id) {
  return getServiceById(id)?.name ?? id;
}

export function formatServicesProse() {
  const names = ALL_SERVICE_NAMES;
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}.`;
}

export function getContactInterests() {
  return [
    { id: ODOO_SERVICE.id, label: ODOO_SERVICE.name },
    ...SUPPORTING_SERVICES.map(({ id, name }) => ({ id, label: name })),
  ];
}

export function getFooterSolutions() {
  return ALL_SERVICE_NAMES;
}

/** Services on concentric rings — Odoo innermost, AI outermost */
export function getOrbitLayout() {
  const items = [ODOO_SERVICE, ...SUPPORTING_SERVICES];
  return items.map((item, i) => ({
    id: item.id,
    label: item.name,
    color: item.id === 'odoo' ? ODOO_PURPLE : item.color,
    angle: ORBIT_ANGLES[i],
    radius: ORBIT_RINGS[i],
    ring: i + 1,
  }));
}

export function getOrbitLabelStyle(angle, radius, cx = 260, cy = 260, viewSize = 520) {
  const rad = (angle * Math.PI) / 180;
  const labelR = radius + 30;
  const lx = cx + Math.cos(rad) * labelR;
  const ly = cy + Math.sin(rad) * labelR;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  let transform = 'translate(-50%, -50%)';
  if (cos > 0.25) transform = 'translate(8px, -50%)';
  else if (cos < -0.25) transform = 'translate(calc(-100% - 8px), -50%)';
  else if (sin < -0.25) transform = 'translate(-50%, calc(-100% - 8px))';
  else if (sin > 0.25) transform = 'translate(-50%, 8px)';

  return {
    left: `${(lx / viewSize) * 100}%`,
    top: `${(ly / viewSize) * 100}%`,
    transform,
  };
}

/** @deprecated use getOrbitLayout */
export function getOrbitServices() {
  return getOrbitLayout();
}

export function getPortfolioFilters() {
  return ['All', ...ALL_SERVICE_NAMES];
}

export function buildFooterLinkMap(extra = {}) {
  return {
    About: 'about',
    Contact: 'contact',
    'odoo Modules': 'odoo',
    'odoo Modules': 'odoo',
    FAQ: 'contact',
    Insights: 'about',
    Partners: 'contact',
    Press: 'contact',
    [ODOO_SERVICE.name]: ODOO_SERVICE.scrollTarget,
    ...Object.fromEntries(SUPPORTING_SERVICES.map((s) => [s.name, s.scrollTarget])),
    ...extra,
  };
}
